import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Clapperboard, Crown, ShieldCheck, X } from "lucide-react";
import type { PresencePayload, PresenceViewer } from "@wedding/shared";
import { cn } from "./lib/cn";
import { AvatarBubble } from "./avatar-bubble";

/**
 * Director first ("king"), then guests in join order — so the crown is always
 * visible, even past the facepile cap and at the top of the audience list.
 * Array.sort is stable, so guests keep their join order.
 */
export function orderViewers(presence: PresencePayload): PresenceViewer[] {
  // Leaders first (director, then admin), then guests in join order.
  const rank = (r: PresenceViewer["role"]) => (r === "reji" ? 0 : r === "admin" ? 1 : 2);
  return [...presence.viewers].sort((a, b) => rank(a.role) - rank(b.role));
}

/**
 * The full viewer list (crown rows, amber "Reji" chip, guest fallback name).
 * Exported so the Reji xl docked rail can render it without the bottom sheet
 * chrome — one canonical audience list everywhere.
 */
export function AudienceList({ presence }: { presence: PresencePayload | null }) {
  const { t } = useTranslation();
  const ordered = presence ? orderViewers(presence) : [];
  const total = presence?.total ?? 0;
  const listed = presence?.viewers.length ?? 0;

  return (
    <ul className="overflow-y-auto no-scrollbar px-2 pb-2">
      {ordered.map((v) => (
        <li key={v.id} className="flex items-center gap-3 px-3 py-2.5">
          <span className="relative shrink-0">
            <AvatarBubble
              name={v.name}
              className={cn(
                "size-9 text-sm ring-1 ring-white/10",
                v.role === "reji" && "ring-2 ring-amber-300/70",
                v.role === "admin" && "ring-2 ring-sky-300/70",
              )}
            />
            {v.role === "reji" && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center size-3.5 rounded-full bg-amber-300/90 text-black ring-1 ring-[#141210]">
                <Crown className="size-2.5" strokeWidth={2.5} />
              </span>
            )}
            {v.role === "admin" && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center size-3.5 rounded-full bg-sky-300/90 text-black ring-1 ring-[#141210]">
                <ShieldCheck className="size-2.5" strokeWidth={2.5} />
              </span>
            )}
          </span>
          <span className="text-white/85 text-sm truncate">
            {v.role === "admin"
              ? t("admin.presenceName")
              : v.name || t("slideshow.guestFallbackName")}
          </span>
          {v.role === "reji" && (
            <span className="ml-auto shrink-0 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-300 bg-amber-400/15 border border-amber-300/30 rounded-full px-2 py-0.5">
              <Clapperboard className="h-3 w-3" /> Reji
            </span>
          )}
          {v.role === "admin" && (
            <span className="ml-auto shrink-0 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-sky-300 bg-sky-400/15 border border-sky-300/30 rounded-full px-2 py-0.5">
              <ShieldCheck className="h-3 w-3" /> Admin
            </span>
          )}
        </li>
      ))}
      {total > listed && (
        <li className="px-3 py-2 text-white/45 text-xs">
          {t("slideshow.presenceMore", { count: total - listed })}
        </li>
      )}
    </ul>
  );
}

interface AudienceSheetProps {
  presence: PresencePayload;
  onClose: () => void;
  /** Portal stacking — guest cinematic surface = z-[120]; Reji = z-[70]. */
  zClassName?: string;
}

/**
 * Bottom sheet (mainstream mobile pattern) listing everyone on the Sahne.
 * Portalled above the cinematic surface. Total lives in the header + the
 * facepile aria-label; the rows are the canonical AudienceList.
 */
export function AudienceSheet({ presence, onClose, zClassName = "z-[120]" }: AudienceSheetProps) {
  const { t } = useTranslation();
  return createPortal(
    <div className={cn("fixed inset-0", zClassName)} role="dialog" aria-modal="true">
      <button
        aria-hidden
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 cursor-default"
      />
      <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-[#141210]/95 backdrop-blur-xl border-t border-white/12 max-h-[70vh] flex flex-col safe-bottom">
        <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-white/20" />
        <div className="flex items-center justify-between px-5 py-3.5">
          <p className="text-white/90 text-sm font-semibold uppercase tracking-[0.2em]">
            {t("slideshow.presenceTitle")}
            <span className="ml-2 text-white/45 tabular-nums normal-case">{presence.total}</span>
          </p>
          <button
            onClick={onClose}
            aria-label={t("common.close", { defaultValue: "Kapat" })}
            className="text-white/55 hover:text-white transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <AudienceList presence={presence} />
      </div>
    </div>,
    document.body,
  );
}
